/**
 * Bind Quote Panel (PR #5c).
 *
 * Displayed in the right-hand sidebar of the Commercial PA workbench
 * once a quote has been calculated and the user holds `quotes.bind`.
 *
 * Shows the calculated gross premium and a "Bind quote" button. On
 * success it renders a confirmation receipt with the bound reference.
 * On authority-related errors it shows a friendly inline message via
 * the toast system (the parent also has the toast).
 *
 * The panel is intentionally stateless w.r.t. the quote itself — it
 * receives everything it needs from the parent (App.tsx) through props.
 */

import React, { useState } from 'react';
import type { AppliedFacility, QuoteBindRequest, QuoteBindResponse } from '@cortex/domain';
import { formatMoney } from '@/components/ui/CurrencyValue';

interface BindQuotePanelProps {
  readonly context: Record<string, unknown>;
  readonly appliedFacility: AppliedFacility | null;
  readonly grossPremium: number;
  readonly quoteRef: string;
  readonly onBind: (req: QuoteBindRequest) => Promise<QuoteBindResponse>;
  readonly onNewQuote: () => void;
}

export const BindQuotePanel: React.FC<BindQuotePanelProps> = ({
  context,
  appliedFacility,
  grossPremium,
  quoteRef,
  onBind,
  onNewQuote,
}) => {
  const [isBinding, setIsBinding] = useState(false);
  const [confirmation, setConfirmation] = useState<QuoteBindResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleBind = async () => {
    setIsBinding(true);
    setErrorMessage(null);
    try {
      const req: QuoteBindRequest = {
        context: context as QuoteBindRequest['context'],
        appliedFacility,
      };
      const result = await onBind(req);
      setConfirmation(result);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Bind failed. Please retry or refer this quote.';
      setErrorMessage(msg);
    } finally {
      setIsBinding(false);
    }
  };

  if (confirmation) {
    return (
      <section
        aria-label="Quote bound confirmation"
        className="rounded-xl border border-emerald-200 bg-emerald-50 shadow-md overflow-hidden font-sans"
      >
        <div className="bg-emerald-700 px-5 py-3 text-white flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M5 13l4 4L19 7"
            />
          </svg>
          <span className="text-xs font-bold uppercase tracking-widest">Quote Bound</span>
        </div>
        <div className="p-5 space-y-3">
          <dl className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <dt className="font-bold text-slate-500 uppercase tracking-widest text-micro mb-0.5">
                Reference
              </dt>
              <dd className="font-mono text-emerald-800 font-bold">{confirmation.quoteRef}</dd>
            </div>
            <div>
              <dt className="font-bold text-slate-500 uppercase tracking-widest text-micro mb-0.5">
                Gross premium
              </dt>
              <dd className="font-mono text-emerald-800 font-bold">
                {formatMoney(confirmation.grossPremium)}
              </dd>
            </div>
            <div className="col-span-2">
              <dt className="font-bold text-slate-500 uppercase tracking-widest text-micro mb-0.5">
                Bound at
              </dt>
              <dd className="text-slate-700">
                {new Date(confirmation.boundAt).toLocaleString('en-GB', {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
              </dd>
            </div>
          </dl>
          <button
            type="button"
            onClick={onNewQuote}
            className="mt-2 w-full rounded-md border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors"
          >
            Start new quote
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      aria-label="Bind quote"
      className="rounded-xl border border-slate-200 bg-white shadow-md overflow-hidden font-sans"
    >
      <div className="bg-hr-indigo/95 px-5 py-3 text-white flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-widest">Bind authority</span>
        <span className="text-micro font-mono text-white/60">{quoteRef}</span>
      </div>
      <div className="p-5 space-y-4">
        <div>
          <p className="text-micro font-bold text-slate-500 uppercase tracking-widest mb-1">
            Gross premium
          </p>
          <p className="text-xl font-bold text-hr-indigo tabular-nums tracking-tight">
            {formatMoney(grossPremium)}
          </p>
        </div>

        {errorMessage && (
          <div className="rounded border border-destructive/20 bg-destructive/10 p-3 text-xs text-destructive">
            {errorMessage}
          </div>
        )}

        <button
          type="button"
          onClick={handleBind}
          disabled={isBinding}
          className="w-full rounded-md bg-hr-indigo px-4 py-2 text-xs font-bold text-white uppercase tracking-widest hover:bg-hr-indigo/90 disabled:opacity-50 transition-colors"
        >
          {isBinding ? 'Binding…' : 'Bind quote'}
        </button>
      </div>
    </section>
  );
};
